//
// Copyright (c) 2011-2021 by Curalate, Inc.
//

import { PojoMap } from 'pojo-maps';
import { createSelector } from 'reselect';

import {
  IBulkPinScheduleDetails,
  IPinterestPostDetailsPin,
  PinPostingIntervalValue,
} from '@pinterest/redux/post-details/pinterestPostDetailsModel';
import PinId from '@models/PinId';
import { getBulkScheduleDetails } from '@pinterest/redux/post-details/selectors/getBulkScheduleDetails';
import { isDefined } from '@utils/filterPredicates';
import moment from 'moment-timezone';
import { WeekDayValue } from '@utils/moment';
import { getOrderedPostDetailsPins } from '@pinterest/redux/post-details/selectors/getOrderedPostDetailsPins';

export const getBulkPinScheduledTimesImpl = (
  orderedPins: IPinterestPostDetailsPin[],
  bulkScheduleDetails: IBulkPinScheduleDetails,
): PojoMap<PinId, Date> | undefined => {
  const { daysOfWeek, pinsPerDay, postingInterval, startScheduledTime } =
    bulkScheduleDetails;

  if (
    daysOfWeek.length === 0 ||
    !isDefined(pinsPerDay) ||
    (pinsPerDay > 1 && !isDefined(postingInterval)) ||
    !isDefined(startScheduledTime)
  ) {
    return;
  }

  const scheduledTimes = getScheduledTimesGroupedByDay(
    orderedPins.length,
    startScheduledTime,
    daysOfWeek,
    pinsPerDay,
    postingInterval,
  ).flat();

  const scheduledTimesByPinId = PojoMap.fromEntries(
    orderedPins.map((pin, i) => {
      return [pin.id, scheduledTimes[i] ?? new Date()];
    }),
  );

  return scheduledTimesByPinId;
};

export const getScheduledTimesGroupedByDay = (
  totalNumberOfPins: number,
  startScheduledTime: Date,
  daysOfWeek: WeekDayValue[],
  pinsPerDay: number,
  postingInterval?: PinPostingIntervalValue,
): Date[][] => {
  const numberOfDays = Math.ceil(totalNumberOfPins / pinsPerDay);

  // Get the actual start date in case the selected start date does not fall on a day
  // that the user has selected.
  const actualScheduleStartDate = getStartDateFromDaysOfWeekSelection(
    daysOfWeek,
    startScheduledTime,
  );

  let scheduleStartTime = actualScheduleStartDate;

  const scheduledTimesGroupedByDay: Date[][] = Array.from({
    length: numberOfDays,
  }).map((_) => {
    const scheduledTimesForDay = Array.from({ length: pinsPerDay }, (_, i) => {
      const minutesToAdd = (postingInterval ?? 0) * i;
      return moment(scheduleStartTime).add(minutesToAdd, 'minutes').toDate();
    });

    scheduleStartTime = getNextScheduleDate(daysOfWeek, scheduleStartTime);

    return scheduledTimesForDay;
  });

  return scheduledTimesGroupedByDay;
};

/**
 * A helper to get the next scheduled date based on the selected days
 * of the week and current scheduled date.
 */
const getNextScheduleDate = (
  daysOfWeek: WeekDayValue[],
  currScheduleDate: Date,
): Date => {
  const currScheduledDateMoment = moment(currScheduleDate);

  const currScheduledDateDayOfWeek =
    currScheduledDateMoment.day() as WeekDayValue;

  // For all selected days prior to the day of the current date, add 7 to compare them all as "future" days.
  const upcomingSelectedDaysOfWeek = daysOfWeek.map((d) =>
    d <= currScheduledDateDayOfWeek ? d + 7 : d,
  );
  const nextSelectedDayOfWeek = Math.min(...upcomingSelectedDaysOfWeek);

  const dayDifference = nextSelectedDayOfWeek - currScheduledDateDayOfWeek;

  return currScheduledDateMoment.add(dayDifference, 'days').toDate();
};

/**
 * A helper to get the actual start scheduled date based on the selected days
 * of the week and selected start date.
 *
 * If the selected start date falls on a day of the week that has not been selected,
 * returns the date for the next upcoming day of the week that has been selected.
 *
 */
const getStartDateFromDaysOfWeekSelection = (
  daysOfWeek: WeekDayValue[],
  selectedStartDate: Date,
): Date => {
  const selectedStarteDateMoment = moment(selectedStartDate);

  const selectedStartDateDayOfWeek =
    selectedStarteDateMoment.day() as WeekDayValue;
  if (daysOfWeek.includes(selectedStartDateDayOfWeek)) {
    return selectedStartDate;
  }

  return getNextScheduleDate(daysOfWeek, selectedStartDate);
};

/**
 * Gets a map of PinId to scheduled time (Date) for all current pins if all
 * bulk schedule details are filled out.
 */
export const getBulkPinScheduledTimes = createSelector(
  getOrderedPostDetailsPins,
  getBulkScheduleDetails,
  getBulkPinScheduledTimesImpl,
);
